import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, Briefcase, Users, Clock, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INDUSTRIES = ['all', 'Automotive', 'Medical', 'Real Estate', 'Tech', 'Finance', 'Legal', 'Insurance', 'Construction', 'Dental', 'Retail', 'Marketing/Media', 'Manufacturing', 'Food & Hospitality', 'Agriculture', 'Energy', 'Logistics'];
const COMP_TYPES = ['all', 'flat', 'cpm', 'hybrid'];
const PLATFORMS = ['all', 'LinkedIn', 'X', 'Instagram', 'YouTube'];

function CompBadge({ type }) {
  const labels = { flat: 'Flat Fee', cpm: 'CPM', hybrid: 'Flat + Bonus' };
  return (
    <span className="text-xs font-mono uppercase tracking-wider px-2 py-1 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20">
      {labels[type] || type}
    </span>
  );
}

function StatusIndicator({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider ${isActive ? 'text-[#10B981]' : 'text-gray-400'}`}>
      <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-[#10B981] animate-pulse' : 'bg-gray-500'}`} />
      {status}
    </span>
  );
}

export default function CampaignBoard() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [industry, setIndustry] = useState(searchParams.get('industry') || 'all');
  const [compType, setCompType] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, [industry, compType, platform]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industry !== 'all') params.append('industry', industry);
      if (compType !== 'all') params.append('compensation_type', compType);
      if (platform !== 'all') params.append('platform', platform);
      const { data } = await axios.get(`${API}/campaigns?${params.toString()}`);
      setCampaigns(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = campaigns.filter(c =>
    !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.brand_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="campaign-board">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#111827]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8">
          <h1 className="font-display text-3xl sm:text-5xl font-bold mb-2" data-testid="campaigns-heading">
            Open <span className="text-[#FF4343]">Campaigns</span>
          </h1>
          <p className="text-[#9CA3AF] text-base max-w-2xl">
            Browse active campaigns from verified brands. Apply to endorse products you use and trust.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8">
        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <input
              type="text" placeholder="Search campaigns or brands..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="campaign-search"
              className="w-full pl-10 pr-4 py-3 bg-[#111827] border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            data-testid="toggle-filters-btn"
            className="flex items-center gap-2 border border-white/20 bg-transparent text-white hover:bg-white/5 px-5 py-3 text-sm font-medium transition-all duration-200 md:hidden"
          >
            <Filter className="w-4 h-4" /> Filters <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters (Desktop) */}
          <div className={`${filtersOpen ? 'block' : 'hidden'} md:block w-full md:w-56 flex-shrink-0 space-y-6`}>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Industry</label>
              {INDUSTRIES.map(i => (
                <button key={i} onClick={() => setIndustry(i)} data-testid={`filter-industry-${i}`}
                  className={`block w-full text-left px-3 py-2 text-sm transition-colors ${industry === i ? 'text-[#FF4343] bg-[#FF4343]/5' : 'text-[#9CA3AF] hover:text-white'}`}>
                  {i === 'all' ? 'All Industries' : i}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Compensation</label>
              {COMP_TYPES.map(c => (
                <button key={c} onClick={() => setCompType(c)} data-testid={`filter-comp-${c}`}
                  className={`block w-full text-left px-3 py-2 text-sm transition-colors ${compType === c ? 'text-[#FF4343] bg-[#FF4343]/5' : 'text-[#9CA3AF] hover:text-white'}`}>
                  {c === 'all' ? 'All Types' : c === 'flat' ? 'Flat Fee' : c === 'cpm' ? 'CPM' : 'Hybrid'}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Platform</label>
              {PLATFORMS.map(p => (
                <button key={p} onClick={() => setPlatform(p)} data-testid={`filter-platform-${p}`}
                  className={`block w-full text-left px-3 py-2 text-sm transition-colors ${platform === p ? 'text-[#FF4343] bg-[#FF4343]/5' : 'text-[#9CA3AF] hover:text-white'}`}>
                  {p === 'all' ? 'All Platforms' : p}
                </button>
              ))}
            </div>
          </div>

          {/* Campaign Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-[#111827] border border-white/10 p-6 animate-pulse">
                    <div className="h-4 bg-white/10 rounded w-3/4 mb-4" />
                    <div className="h-3 bg-white/10 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-white/10 rounded w-full mb-4" />
                    <div className="h-8 bg-white/10 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <Briefcase className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
                <p className="text-[#9CA3AF]">No campaigns found matching your criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((c, idx) => (
                  <Link
                    key={c.id}
                    to={`/campaigns/${c.id}`}
                    data-testid={`campaign-card-${c.id}`}
                    className={`group bg-[#111827] border border-white/10 hover:border-white/20 transition-all duration-200 block ${c.brand?.verified ? 'border-l-[3px] border-l-[#FF4343]' : ''}`}
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <div className="p-6">
                      {/* Brand & Status */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {c.brand?.logo_url && (
                            <img src={c.brand.logo_url} alt="" className="w-8 h-8 object-cover rounded-sm bg-white/10" />
                          )}
                          <div>
                            <span className="text-xs text-[#9CA3AF]">{c.brand_name}</span>
                            {c.brand?.verified && <span className="ml-1 text-[#FF4343] text-xs">&#10003;</span>}
                          </div>
                        </div>
                        <StatusIndicator status={c.status} />
                      </div>

                      {/* Title */}
                      <h3 className="font-display text-lg font-semibold text-white mb-3 group-hover:text-[#FF4343] transition-colors line-clamp-2">
                        {c.title}
                      </h3>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="text-xs px-2 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{c.industry}</span>
                        {c.platforms?.map(p => (
                          <span key={p} className="text-xs px-2 py-1 bg-white/5 text-[#9CA3AF] border border-white/10">{p}</span>
                        ))}
                      </div>

                      {/* Compensation */}
                      <div className="flex items-center justify-between mb-4">
                        <CompBadge type={c.compensation_type} />
                        <span className="text-sm font-mono text-[#FF4343]">{c.budget_range}</span>
                      </div>

                      {/* Tier & Spots */}
                      <div className="flex items-center justify-between text-xs text-[#6B7280] mb-3">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {c.exec_tier_required}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {c.deadline ? new Date(c.deadline).toLocaleDateString() : 'Open'}
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-[#6B7280]">{c.spots_filled} of {c.spots_total} spots filled</span>
                        </div>
                        <div className="w-full h-1.5 bg-white/5 overflow-hidden">
                          <div
                            className="h-full bg-[#FF4343] transition-all duration-500"
                            style={{ width: `${(c.spots_filled / c.spots_total) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
